import * as React from 'react';
import { useContext, useState, useEffect } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import './RegistrationAuthenticateStep.css';
import { RootStoreContext } from '../../../stores/RootStore';
import API from '../../../api/API';
import { StepName } from 'src/config/Wizard';
import NumberInputWithKeypad from './NumberInputWithKeypad';
import { getPendingLoginOtpRemainingSeconds } from 'src/stores/LoginOtpSession';


const OTPStep = () => {
  const { registrationStore, authStore, wizardStore } = useContext(RootStoreContext);
  const [otpCode, setOtpCode] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(getPendingLoginOtpRemainingSeconds);

  // Timer for OTP expiration countdown
  useEffect(() => {
    if (timeLeft <= 0) return;
    
    const timer = setTimeout(() => {
      setTimeLeft(prevTime => prevTime - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Scroll to top when error occurs
  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  const handleBack = () => {
    registrationStore.clearPendingLoginOtp();
    wizardStore.setStep(StepName.REGISTRATION_AUTHENTICATE);
  }

  const loginMethod = registrationStore.loginMethod;
  const phoneNumber = registrationStore.loginPhoneNumber;
  const email = registrationStore.loginEmail;
  const loginTarget = loginMethod === 'email' ? email : phoneNumber;
  const displayedLoginTarget = registrationStore.displayedLoginTarget || registrationStore.displayedPhoneNumber;

  useEffect(() => {
    if (!loginTarget) {
      wizardStore.setStep(StepName.REGISTRATION_AUTHENTICATE);
    }
  }, [loginTarget, wizardStore]);

  // Handle OTP verification
  const handleVerify = async () => {
    if (otpCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    try {
      setIsVerifying(true);
      setError(null);
      
      const response = await API.post('/login/verify', loginMethod === 'email'
        ? { email, otpCode }
        : { phoneNumber, otpCode });
      
      const data = JSON.parse(response);
      
      // Set the token in auth store
      authStore.login(data.token);
      registrationStore.clearPendingLoginOtp();
      
      // Load user data
      await registrationStore.loadUser(data.identifier || data.phoneNumber || phoneNumber);
      
      // Navigation is handled by the loadUser method which sets the next step
    } catch (err: any) {
      setError(err?.error === 'THROTTLING_OTP'
        ? 'Too many incorrect codes. Please request a new code and try again later.'
        : err?.error || 'Invalid verification code');
      setOtpCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Container className='mt-5'>
      <h1 className="text-center studio-name">Enter verification code</h1>
      <p className="text-center text-muted small description">
        A 6-digit code has been sent to {displayedLoginTarget}
        <br />  
        {loginMethod === 'email' ? 'The email can take up to 2 min to arrive' : 'The SMS can take up to 2 min to arrive'}
      </p>
      
      {error && (
        <Row className="mt-2 mb-2">
          <Col className="text-center">
            <div className="text-danger">{error}</div>
          </Col>
        </Row>
      )}
      
      <Row>
        <Col lg={{span: 4, offset: 4}} md={{span: 6, offset: 3}} sm={{span: 10, offset: 1}} xs={{span: 12, offset: 0}} className="phone">
          <NumberInputWithKeypad
            value={otpCode}
            onChange={setOtpCode}
            placeholder="Enter 6-digit code"
            maxLength={6}
            disabled={isVerifying}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            onEnter={handleVerify}
          />
          
          <Row>
            <Col>
              <Button 
                className="btn-block" 
                variant="info"
                disabled={otpCode.length !== 6 || isVerifying}
                onClick={handleVerify}
              >
                {isVerifying ? 'Verifying...' : 'Verify'}
              </Button>
            </Col>
          </Row>
          
          <Row>
            <Col>
              <Button 
                className="btn-block" 
                variant="outline-secondary"
                onClick={handleBack}
                disabled={isVerifying}
              >
                Back
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default OTPStep;
